import axios from "axios";

import { analyticsContext } from "@/lib/analytics";

// Emergent production serves the frontend and the API from the SAME origin, with /api routed to the
// backend, so a production build must use a relative base. Baking the absolute preview host into the
// production bundle would make the live site call the preview backend and the preview database.
// The preview runs the CRA dev server (NODE_ENV !== "production"), where the absolute host is needed.
const BACKEND_URL =
  process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

// Double-submit CSRF: echo the readable csrf_token cookie back on state-changing requests.
api.interceptors.request.use((config) => {
  if (["post", "patch", "put", "delete"].includes((config.method || "").toLowerCase())) {
    const token = readCookie("csrf_token");
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function track(payload) {
  // Every event carries the anonymous visitor/session context and first-touch attribution, and
  // falls back to the current path so no click is recorded without its originating page.
  const enriched = {
    ...analyticsContext(),
    ...payload,
    page: payload.page || window.location.pathname,
  };
  api.post("/events", enriched).catch(() => {});
}

// Default share image used when a page has no page-specific image (guides pass their hero).
const DEFAULT_OG_IMAGE = "/hhl-og-default.png?v=1";

// Routes the backend reports as indexable:false (hidden hubs, retired tools) are downgraded to
// noindex without touching the page itself. Null for every normal, indexable route.
let robotsOverride = null;

export function setRobotsOverride(value) {
  robotsOverride = value;
  if (!value) return;
  const el = document.head.querySelector('meta[name="robots"]');
  // Only ever tighten: a page that already asked for noindex keeps its own, stricter directive.
  if (!el) {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", value);
    document.head.appendChild(meta);
  } else if (!el.getAttribute("content").includes("noindex")) {
    el.setAttribute("content", value);
  }
}

export function setSeo({ title, description, canonical, image, type = "website", noindex = false, follow = true }) {
  if (title) document.title = title;
  const set = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement(attrs.tag);
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "tag") el.setAttribute(k, v);
    });
  };
  if (description) {
    set('meta[name="description"]', { tag: "meta", name: "description", content: description });
    set('meta[property="og:description"]', { tag: "meta", property: "og:description", content: description });
    set('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: description });
  }
  if (title) {
    set('meta[property="og:title"]', { tag: "meta", property: "og:title", content: title });
    set('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: title });
  }
  set('meta[property="og:type"]', { tag: "meta", property: "og:type", content: type });
  const shareImage = image || window.location.origin + DEFAULT_OG_IMAGE;
  set('meta[property="og:image"]', { tag: "meta", property: "og:image", content: shareImage });
  set('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
  set('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: shareImage });
  set('meta[property="og:site_name"]', { tag: "meta", property: "og:site_name", content: "Human Home Living" });
  // Public pages are indexable. Error / not-found views pass noindex so they stay out of indexes.
  // A route the backend reports as non-indexable (see setRobotsOverride) is downgraded here, but a
  // page asking for something stricter than the override always wins.
  set('meta[name="robots"]', {
    tag: "meta",
    name: "robots",
    content: noindex
      ? (follow ? "noindex, follow" : "noindex, nofollow")
      : robotsOverride || "index, follow",
  });
  // Self-referencing canonical on the serving host.
  const url = canonical || window.location.origin + window.location.pathname;
  set('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
  set('meta[property="og:url"]', { tag: "meta", property: "og:url", content: url });
}

export function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

// Removes a block set by a previously viewed page so schema never leaks across routes.
export function clearJsonLd(id) {
  document.getElementById(id)?.remove();
}
